import { useState } from "react";
import { FadeUp } from "@/components/site/Reveal";
import { COMPANY } from "@/lib/assets";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";

const empty = { name: "", email: "", company: "", phone: "", subject: "Quote Request", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/inquiries", form);
      toast.success("Message sent — our team will respond within one business day.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to send message.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-background border border-border px-4 py-3 focus:border-primary outline-none transition-colors text-foreground";

  return (
    <section id="contact" data-testid="contact-section" className="bg-background py-28 md:py-40 border-t border-border">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <FadeUp>
            <p className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary mb-6">/ Get in touch</p>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">Let's talk supply.</h2>
            <p className="text-muted-foreground mt-8 max-w-md leading-relaxed">
              Qualifying a new grade or scaling an existing programme? Our trade desk responds within one business day.
            </p>

            <ul className="mt-12 space-y-6">
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={20} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                <a href={`mailto:${COMPANY.email}`} className="link-underline">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <Phone size={20} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                <span>{COMPANY.phone}</span>
              </li>
              <li className="flex items-start gap-4 text-muted-foreground" data-testid="contact-address">
                <MapPin size={20} className="text-primary shrink-0 mt-1" strokeWidth={1.5} />
                <span className="max-w-xs leading-relaxed">{COMPANY.address}</span>
              </li>
            </ul>
          </FadeUp>
        </div>

        <div className="lg:col-span-7">
          <FadeUp delay={0.15}>
            <form onSubmit={submit} className="bg-card border border-border p-8 md:p-10" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name *">
                  <input required value={form.name} onChange={update("name")} className={inputCls} data-testid="contact-name-input" />
                </Field>
                <Field label="Email *">
                  <input required type="email" value={form.email} onChange={update("email")} className={inputCls} data-testid="contact-email-input" />
                </Field>
                <Field label="Company">
                  <input value={form.company} onChange={update("company")} className={inputCls} data-testid="contact-company-input" />
                </Field>
                <Field label="Phone">
                  <input value={form.phone} onChange={update("phone")} className={inputCls} data-testid="contact-phone-input" />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Subject">
                  <input value={form.subject} onChange={update("subject")} className={inputCls} data-testid="contact-subject-input" />
                </Field>
              </div>
              <div className="mt-5">
                <Field label="Message *">
                  <textarea required rows={5} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                </Field>
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit-btn"
                className="mt-8 inline-flex items-center gap-2 bg-foreground text-background px-8 py-4 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-primary transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Sending…" : "Send message"} <ArrowUpRight size={15} />
              </button>
            </form>
          </FadeUp>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground block mb-2">{label}</span>
    {children}
  </label>
);
